<script setup lang="ts">
import { CheckboxField, HelpTextTooltip, TextField } from '@haskoning/vue-component-library'
import { useCalculationFormComposable } from '@/components/calculation-form/composable/useCalculationFormComposable'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import { defineProps } from 'vue'
import { VESSEL_LOAD_CASE_FIELDS } from '@/components/calculation-form/constants/constants'
import { useShipStore } from '@/stores/ship'

const props = defineProps<CalculationFormMixinProps>()
const emit = defineEmits(['updateTabCompletion'])
const {
  calculationStore,
  currentItem,
  isValid,
  formRef,
  disabled,
  isReadOnly,
  hasHelpText,
  lowerValidator,
  upperValidator
} = useCalculationFormComposable(props, emit)
defineExpose({ formRef })

const shipStore = useShipStore()

const shipEngineNumberValidator = (
  value: number,
  ship_field: 'main_engine_number' | 'transverse_thruster_number'
) => {
  for (const shipId of currentItem.value.vessel) {
    const selectedShip = shipStore.getItemById(shipId)
    if (selectedShip && (selectedShip[ship_field] ?? 0) < value) {
      return 'Higher than the ship.'
    }
  }
  return true
}

const customErrorRules = (value: number, key: string) => {
  if (
    key.includes('main_engine_applied_power') ||
    key.includes('transverse_thruster_applied_power')
  ) {
    if (value <= 0) {
      return 'Applied power must be a positive value greater than 0.'
    }
    if (value > 100) {
      return 'Applied power cannot exceed 100%.'
    }
  }

  if (key.includes('main_engine_number') && !key.includes('_step')) {
    if (value > 2) {
      return 'Maximum of 2 main engines allowed.'
    }
    return shipEngineNumberValidator(value, 'main_engine_number')
  }
  if (key.includes('transverse_thruster_number') && !key.includes('_step')) {
    if (value > 2) {
      return 'Maximum of 2 transverse thrusters allowed.'
    }
    return shipEngineNumberValidator(value, 'transverse_thruster_number')
  }
  return true
}
</script>

<template>
  <v-form ref="formRef" v-model="isValid" :disabled="disabled" :readonly="isReadOnly">
    <v-card elevation="0" flat>
      <v-card-title class="header-font d-flex justify-space-between align-center">
        Vessel load case
        <div class="d-flex justify-end">
          <slot name="resetInputButton" />
        </div>
      </v-card-title>
      <v-card-subtitle class="description-font"> Variable vessel parameters</v-card-subtitle>

      <v-card-text>
        <v-row
          align="start"
          no-gutters
          v-for="field in VESSEL_LOAD_CASE_FIELDS"
          :key="field.INDIVIDUAL_VALUE"
        >
          <v-col cols="12" md="8" lg="4" class="d-flex align-center pt-7">
            <HelpTextTooltip
              v-if="hasHelpText(field.INDIVIDUAL_VALUE)"
              :text="calculationStore.fields[field.INDIVIDUAL_VALUE].help_text || ''"
            />
            <span :class="hasHelpText(field.INDIVIDUAL_VALUE) ? 'px-2' : 'px-0'">
              {{ calculationStore.fields[field.INDIVIDUAL_VALUE].label }}
            </span>
          </v-col>

          <v-col cols="12" md="8" lg="4" class="pt-0">
            <TextField
              v-if="!currentItem[field.RANGE_INDICATOR]"
              :field="calculationStore.fields[field.INDIVIDUAL_VALUE]"
              :model-value="currentItem"
              :no-help-text="true"
              :readonly="isReadOnly"
              :custom-error-rules="[
                (value: number) => customErrorRules(value, field.INDIVIDUAL_VALUE)
              ]"
              @update="calculationStore.update(currentItem)"
            />

            <template v-else>
              <v-row align="center" no-gutters class="ga-4">
                <v-col v-for="(key, index) in field.RANGE_VALUES" :key="key">
                  <TextField
                    :field="calculationStore.fields[key]"
                    :model-value="currentItem"
                    :label="['min', 'max', 'step'][index]"
                    :no-help-text="true"
                    :readonly="isReadOnly"
                    :custom-error-rules="[
                      {
                        0: (value: number) => lowerValidator(value, key),
                        1: (value: number) => upperValidator(value, key)
                      }[index] || ((value: number) => true),
                      (value: number) => customErrorRules(value, key)
                    ]"
                    @update="calculationStore.update(currentItem)"
                  />
                </v-col>
              </v-row>
            </template>

            <CheckboxField
              :field="calculationStore.fields[field.RANGE_INDICATOR]"
              :model-value="currentItem"
              :no-help-text="true"
              :readonly="isReadOnly"
              @update="calculationStore.update(currentItem)"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<style scoped>
.v-input :deep(.v-input__control) {
  margin-left: -10px;
}

.v-input :deep(.v-input__details) {
  display: none;
}
</style>
